import { useState } from 'react';
import { Link } from 'react-router-dom';
import { GraduationCap, Loader2, UserCheck } from 'lucide-react';
import api from '@/lib/api';
import { useAuthStore } from '@/stores/auth.store';
import { cn } from '@/lib/utils';

export type SelfAssignResourceKind = 'podcasts' | 'eduflows';

type Props = {
  kind: SelfAssignResourceKind;
  resourceId: string;
  assignedUserIds: string[];
  onAssigned: () => void | Promise<void>;
  className?: string;
};

export default function SelfAssignAsLearnerCard({
  kind,
  resourceId,
  assignedUserIds,
  onAssigned,
  className,
}: Props) {
  const user = useAuthStore((s) => s.user);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!user) return null;

  const isAssigned = assignedUserIds.includes(user.id);

  const handleSelfAssign = async () => {
    setLoading(true);
    setError(null);
    try {
      await api.post(`/${kind}/${resourceId}/learners/self`);
      await onAssigned();
    } catch (err: unknown) {
      const msg =
        err && typeof err === 'object' && 'message' in err && typeof (err as { message: string }).message === 'string'
          ? (err as { message: string }).message
          : 'Impossible de vous assigner comme apprenant.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className={cn(
        'rounded-xl border border-dashed border-[color:color-mix(in_srgb,var(--inst-player-primary,#0C1C38)_35%,transparent)]',
        'bg-[color:color-mix(in_srgb,var(--inst-player-primary,#0C1C38)_6%,transparent)] p-4',
        className,
      )}
    >
      <div className="flex items-start gap-3">
        <span
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white text-[color:var(--inst-player-primary,#0C1C38)] shadow-sm dark:bg-gray-900"
          aria-hidden
        >
          <GraduationCap className="h-5 w-5" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-gray-900 dark:text-white">M&apos;assigner comme apprenant</p>
          <p className="mt-1 text-xs leading-relaxed text-gray-600 dark:text-gray-400">
            Testez le contenu depuis votre espace apprenant, comme vos participants.
          </p>
          {error ? (
            <p className="mt-2 text-xs text-red-600 dark:text-red-400" role="alert">
              {error}
            </p>
          ) : null}
          <div className="mt-3 flex flex-wrap gap-2">
            {isAssigned ? (
              <>
                <span className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-50 px-2.5 py-1.5 text-xs font-medium text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300">
                  <UserCheck className="h-3.5 w-3.5" aria-hidden />
                  Vous êtes assigné
                </span>
                <Link
                  to="/learner"
                  className="inline-flex items-center gap-1.5 rounded-lg bg-[color:var(--inst-player-primary,#0C1C38)] px-3 py-1.5 text-xs font-medium text-white hover:bg-[color:var(--inst-player-primary-hover,#19335E)]"
                >
                  Ouvrir l&apos;espace apprenant
                </Link>
              </>
            ) : (
              <button
                type="button"
                disabled={loading}
                onClick={() => void handleSelfAssign()}
                className="inline-flex items-center gap-1.5 rounded-lg bg-[color:var(--inst-player-primary,#0C1C38)] px-3 py-1.5 text-xs font-medium text-white hover:bg-[color:var(--inst-player-primary-hover,#19335E)] disabled:opacity-60"
              >
                {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden /> : <UserCheck className="h-3.5 w-3.5" aria-hidden />}
                M&apos;assigner
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
